import { Button, Errors, Form, Input, Label, TextField } from '@troon/ui';
import { useTrackEvent } from '@troon/analytics';
import debounce from 'debounce';
import { Switch, Match, createSignal, For, Show } from 'solid-js';
import { Navigate, useLocation } from '@solidjs/router';
import { unwrap } from 'solid-js/store';
import { clientAction } from '@troon/api-client';
import { useAuthStore } from '../flow';
import { SharedPasswordEntry } from './shared-password-entry';
import type { AuthCompleteCredentials } from '../auth';

function createOnce(fn: () => void): () => void {
	const [called, setCalled] = createSignal(false);
	// eslint-disable-next-line solid/reactivity
	return () => {
		if (!called()) {
			fn();
			setCalled(true);
		}
	};
}

type Props = {
	onComplete?: (credentials: AuthCompleteCredentials) => void;
};

export function Join(props: Props) {
	const [store, setStore] = useAuthStore();
	const trackEvent = useTrackEvent();
	const location = useLocation();
	const action = clientAction(
		'post',
		'/v0/auth/signup',
		{},
		{
			transformer: () => ({ embedFacilitySlug: 'troon_app' }),
			revalidate: undefined,
			onSuccess: async (res) => {
				trackEvent('signup', {
					$set: { email: res.user.email, fullName: `${(res.user.firstName, res.user.lastName)}` },
					email: res.user.email,
				});
				props.onComplete?.({
					accessToken: res.accessToken,
					refreshToken: res.refreshToken,
					user: res.user,
				});
			},
		},
	);

	const handleSetFirstName = debounce((value: string) => {
		setStore('data', 'firstName', value);
	});
	const handleSetLastName = debounce((value: string) => {
		setStore('data', 'lastName', value);
	});

	const [valid, setValid] = createSignal(false);
	const trackName = createOnce(() => {
		trackEvent('signupEnteredName', { email: store.data.email });
	});
	const trackPostalCode = createOnce(() => {
		trackEvent('signupEnteredZipcode', { email: store.data.email });
	});
	const trackPassword = createOnce(() => {
		trackEvent('signupEnteredPassword', { email: store.data.email });
	});

	return (
		<Switch>
			<Match when={!store.data.email}>
				<Navigate href={`/auth${location.search}`} />
			</Match>
			<Match when={true}>
				<div class="@container">
					<Form action={action} class="grid gap-4 @lg:grid-cols-2">
						<For each={Object.entries(unwrap(store.data))}>
							{([key, value]) => (
								<Show when={value}>
									<input type="hidden" name={key} value={value} />
								</Show>
							)}
						</For>

						<TextField name="firstName">
							<Label suppressRequired>First name</Label>
							<Input
								onInput={(e) => {
									handleSetFirstName(e.currentTarget.value);
									if (store.data.lastName && e.currentTarget.value) {
										trackName();
									}
								}}
							/>
						</TextField>

						<TextField name="lastName">
							<Label suppressRequired>Last name</Label>
							<Input
								onInput={(e) => {
									handleSetLastName(e.currentTarget.value);
									if (store.data.firstName && e.currentTarget.value) {
										trackName();
									}
								}}
							/>
						</TextField>

						<TextField name="zipcode" class="@lg:col-span-2">
							<Label suppressRequired>Postal code</Label>
							<Input inputMode="numeric" onInput={trackPostalCode} />
						</TextField>

						<div class="flex flex-col gap-4 @lg:col-span-2">
							<SharedPasswordEntry
								onUpdate={(valid) => {
									setValid(valid);
									trackPassword();
								}}
							/>
						</div>

						<div class="@lg:col-span-2">
							<Errors />
						</div>

						<div class="@lg:col-span-2">
							<Button type="submit" disabled={!valid()} class="w-fit">
								Continue
							</Button>
						</div>
					</Form>
				</div>
			</Match>
		</Switch>
	);
}
